import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import FormModal from '../../components/Modals/FormModal';
import { useRole } from '../../hooks/useRole';
import UserBasicInfo from './UserForm/UserBasicInfo';
import UserPasswordSection from './UserForm/UserPasswordSection';
import UserRoleSelector from './UserForm/UserRoleSelector';
import UserBranchSelector from './UserForm/UserBranchSelector';
import UserSubmitButton from './UserForm/UserSubmitButton';
import { useUser } from '../../hooks/useUser';
import { evaluatePassword } from '../../utils/passwordStrength';
import BranchService from '../../services/branch/BranchService';
import { IUserForm } from '../../types/user/userInterfaces';

interface UserRegistrationPopupProps {
  popupOpen: boolean;
  onClose: () => void;
  onRegister: (userData: IUserForm) => void;
}

const UserRegistrationPopup: React.FC<UserRegistrationPopupProps> = ({
  popupOpen,
  onClose,
  onRegister,
}) => {
  const { fetchRoles, dropdownOptions } = useRole();
  const [branchOptions, setBranchOptions] = useState<{ id: number; name: string }[]>([]);

  const { form, updateField, isSubmitting, errors, setErrors } = useUser();

  useEffect(() => {
    if (popupOpen) {
      fetchRoles();
      BranchService.fetchDropdown().then(setBranchOptions);
    }
  }, [popupOpen]);

  // ✅ Validación usando IUserForm
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Username
    if (!form.username || form.username.trim().length < 3) {
      newErrors.username = 'El nombre de usuario debe tener al menos 3 caracteres.';
    }

    // Email
    if (
      !form.email ||
      !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email.trim())
    ) {
      newErrors.email = 'Correo electrónico no válido.';
    }

    // Password
    if (!form.password) {
      newErrors.password = 'La contraseña es obligatoria.';
    } else if (!evaluatePassword(form.password).isAcceptable) {
      newErrors.password =
        'La contraseña no cumple los requisitos mínimos de seguridad.';
    }

    // Confirm password
    if ((form.password ?? '') !== (form.confirmPassword ?? '')) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden.';
    }

    // Roles
    if (!form.roleIds || form.roleIds.length === 0) {
      newErrors.roleIds = 'Debe seleccionar al menos un rol.';
    }

    // Sucursales
    if (!form.branchIds || form.branchIds.length === 0) {
      newErrors.branchIds = 'Debe seleccionar al menos una sucursal.';
    }

    // Guardamos en Redux:
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onRegister(form);
  };

 

  return (
    <FormModal
      isOpen={popupOpen}
      onClose={onClose}
      title="Registrar Usuario"
      maxWidth="max-w-4xl"
    >
      <form className="flex flex-col gap-5">
        <UserBasicInfo form={form} onChange={updateField} errors={errors} />

        <section className="rounded-xl border border-stroke bg-white p-5 dark:border-strokedark dark:bg-boxdark-2">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold text-black dark:text-white">
              Accesos
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <UserRoleSelector
              form={form}
              options={dropdownOptions}
              onChange={updateField}
              errors={errors}
            />
            <UserBranchSelector
              form={form}
              options={branchOptions}
              onChange={updateField}
              errors={errors}
            />
          </div>
        </section>

        <UserPasswordSection form={form} onChange={updateField} errors={errors} />

        <UserSubmitButton
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onCancel={onClose}
          label="Registrar Usuario"
        />
      </form>
    </FormModal>
  );
};

export default UserRegistrationPopup;
